import { Head, useForm, Link } from "@inertiajs/react"
import { IconInnerShadowTop } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface RegisterProps {
  errors?: {
    email_address?: string[]
    password?: string[]
    password_confirmation?: string[]
  }
}

export default function Register({ errors }: RegisterProps) {
  const form = useForm({
    email_address: "",
    password: "",
    password_confirmation: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post("/registration")
  }

  type FieldName = "email_address" | "password" | "password_confirmation"

  const fieldError = (field: FieldName) => {
    return errors?.[field]?.[0]
  }

  return (
    <div className="relative flex min-h-svh flex-col bg-background px-6 pt-8 pb-6 md:items-center md:justify-center md:p-10">
      <Head title="Sign Up" />

      <div
        className="absolute inset-0 hidden bg-cover bg-center opacity-40 blur-[3px] md:block"
        style={{ backgroundImage: "url('/images/hops-background.jpg')" }}
      />
      <div className="absolute inset-0 hidden bg-gradient-to-b from-background via-background/60 to-background/40 md:block" />

      <div className="relative z-10 flex w-full flex-1 flex-col gap-6 md:max-w-3xl md:flex-none">
        <Link href="/" className="flex items-center gap-2 self-center font-medium">
          <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground md:size-6">
            <IconInnerShadowTop className="size-5 md:size-4" />
          </div>
          <span className="text-base font-semibold md:text-sm md:font-medium">BLE Manager</span>
        </Link>

        <div className="flex flex-1 flex-col justify-center md:flex-none">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 md:hidden">
            <div className="flex flex-col items-center text-center">
              <h1 className="text-3xl font-bold">Create an account</h1>
              <p className="mt-1 text-balance text-muted-foreground">
                Enter your details to get started
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                size="lg"
                placeholder="email@example.com"
                className="placeholder:text-foreground/50"
                value={form.data.email_address}
                onChange={(e) => form.setData("email_address", e.target.value)}
                required
                autoFocus
                autoComplete="email"
                inputMode="email"
              />
              {fieldError("email_address") && (
                <p className="text-sm text-destructive">{fieldError("email_address")}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                size="lg"
                placeholder="******"
                className="placeholder:text-foreground/50"
                value={form.data.password}
                onChange={(e) => form.setData("password", e.target.value)}
                required
                autoComplete="new-password"
              />
              {fieldError("password") && (
                <p className="text-sm text-destructive">{fieldError("password")}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password_confirmation">Confirm Password</Label>
              <Input
                id="password_confirmation"
                type="password"
                size="lg"
                placeholder="******"
                className="placeholder:text-foreground/50"
                value={form.data.password_confirmation}
                onChange={(e) => form.setData("password_confirmation", e.target.value)}
                required
                autoComplete="new-password"
              />
              {fieldError("password_confirmation") && (
                <p className="text-sm text-destructive">{fieldError("password_confirmation")}</p>
              )}
            </div>
            <Button
              type="submit"
              size="touch-lg"
              className="w-full"
              disabled={form.processing}
            >
              {form.processing ? "Creating account..." : "Sign Up"}
            </Button>
            <Button asChild variant="outline" size="touch-lg" className="w-full">
              <Link href="/session/new">Sign in to existing account</Link>
            </Button>
            <p className="mt-2 text-balance text-center text-xs text-muted-foreground">
              By signing up, you agree to our{" "}
              <span
                aria-disabled="true"
                className="cursor-not-allowed underline underline-offset-4 opacity-60"
              >
                Terms of Service
              </span>{" "}
              and{" "}
              <span
                aria-disabled="true"
                className="cursor-not-allowed underline underline-offset-4 opacity-60"
              >
                Privacy Policy
              </span>
              .
            </p>
          </form>

          <Card className="hidden overflow-hidden p-0 md:block">
            <CardContent className="grid min-h-[480px] items-stretch p-0 md:grid-cols-2">
              <form
                onSubmit={handleSubmit}
                className="flex flex-col justify-center p-6 md:p-8"
              >
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col items-center text-center">
                    <h1 className="text-2xl font-bold">Create an account</h1>
                    <p className="text-balance text-muted-foreground">
                      Enter your details to get started
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email-desktop">Email</Label>
                    <Input
                      id="email-desktop"
                      type="email"
                      placeholder="email@example.com"
                      className="placeholder:text-foreground/50"
                      value={form.data.email_address}
                      onChange={(e) => form.setData("email_address", e.target.value)}
                      required
                      autoComplete="email"
                      inputMode="email"
                    />
                    {fieldError("email_address") && (
                      <p className="text-sm text-destructive">{fieldError("email_address")}</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password-desktop">Password</Label>
                    <Input
                      id="password-desktop"
                      type="password"
                      placeholder="******"
                      className="placeholder:text-foreground/50"
                      value={form.data.password}
                      onChange={(e) => form.setData("password", e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                    {fieldError("password") && (
                      <p className="text-sm text-destructive">{fieldError("password")}</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password_confirmation-desktop">Confirm Password</Label>
                    <Input
                      id="password_confirmation-desktop"
                      type="password"
                      placeholder="******"
                      className="placeholder:text-foreground/50"
                      value={form.data.password_confirmation}
                      onChange={(e) => form.setData("password_confirmation", e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                    {fieldError("password_confirmation") && (
                      <p className="text-sm text-destructive">{fieldError("password_confirmation")}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={form.processing}>
                    {form.processing ? "Creating account..." : "Sign Up"}
                  </Button>
                  <div className="text-center text-sm">
                    Already have an account?{" "}
                    <Link href="/session/new" className="underline underline-offset-4">
                      Login
                    </Link>
                  </div>
                </div>
              </form>
              <div className="hidden md:block">
                <img
                  src="/images/hops-side.jpg"
                  alt="Fresh hops on the vine"
                  className="h-full w-full object-cover brightness-[0.9] dark:brightness-[0.7]"
                />
              </div>
            </CardContent>
          </Card>
          <div className="hidden text-balance text-center text-xs text-muted-foreground md:block">
            By signing up, you agree to our{" "}
            <span
              aria-disabled="true"
              className="cursor-not-allowed underline underline-offset-4 opacity-60"
            >
              Terms of Service
            </span>{" "}
            and{" "}
            <span
              aria-disabled="true"
              className="cursor-not-allowed underline underline-offset-4 opacity-60"
            >
              Privacy Policy
            </span>
            .
          </div>
        </div>
      </div>
    </div>
  )
}
